import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { type User } from 'firebase/auth';
import { auth, logout } from '../services/firebase';

function UserAvatar({ user, size = 36 }: { user: User; size?: number }) {
  if (user.photoURL) {
    return (
      <img
        src={user.photoURL}
        alt=""
        referrerPolicy="no-referrer"
        style={{ width: size, height: size, borderRadius: '50%', border: '2px solid #059669', objectFit: 'cover' }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #059669, #10B981)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: 'white',
      border: '2px solid #059669', flexShrink: 0, userSelect: 'none',
    }}>
      {(user.displayName ?? user.email ?? 'U').charAt(0).toUpperCase()}
    </div>
  );
}

function ProfileDropdown({ user }: { user: User }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: open ? '#F1F5F9' : 'transparent',
          border: '1px solid',
          borderColor: open ? '#E2E8F0' : 'transparent',
          borderRadius: '40px',
          padding: '4px 12px 4px 4px',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.borderColor = '#E2E8F0'; } }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}
      >
        <UserAvatar user={user} size={32} />
        <div style={{ textAlign: 'left' }}>
          <div style={{ color: '#1E293B', fontSize: '13px', fontWeight: 600, lineHeight: 1.2 }}>
            {user.displayName ?? user.email?.split('@')[0] ?? 'User'}
          </div>
          <div style={{ color: '#94A3B8', fontSize: '11px', lineHeight: 1.2 }}>
            {user.email}
          </div>
        </div>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ marginLeft: '2px', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: '260px',
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          zIndex: 100,
        }}>
          {/* Profile header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <UserAvatar user={user} size={44} />
            <div>
              <div style={{ color: '#0F172A', fontSize: '14px', fontWeight: 600 }}>
                {user.displayName ?? user.email?.split('@')[0] ?? 'User'}
              </div>
              <div style={{ color: '#94A3B8', fontSize: '12px', marginTop: '2px' }}>
                {user.email}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                marginTop: '6px', background: 'rgba(5,150,105,0.15)',
                border: '1px solid rgba(5,150,105,0.3)',
                borderRadius: '20px', padding: '2px 8px',
              }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#059669' }} />
                <span style={{ color: '#059669', fontSize: '10px', fontWeight: 600 }}>Active Session</span>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: '8px' }}>
            <DropdownItem icon={<ProfileIcon />} label="My Profile" onClick={() => setOpen(false)} />
            <DropdownItem icon={<SettingsIcon />} label="Settings" onClick={() => setOpen(false)} />
            <DropdownItem icon={<SecurityIcon />} label="Security" onClick={() => setOpen(false)} />
          </div>

          {/* Divider + Sign out */}
          <div style={{ borderTop: '1px solid #F1F5F9', padding: '8px' }}>
            <button
              onClick={() => { setOpen(false); logout(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                padding: '9px 12px', borderRadius: '8px', border: 'none',
                background: 'transparent', cursor: 'pointer', transition: 'all 0.15s',
                color: '#EF4444', fontSize: '13px', fontWeight: 500,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <SignOutIcon />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DropdownItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 12px', borderRadius: '8px', border: 'none',
        background: hovered ? '#F8FAFC' : 'transparent',
        cursor: 'pointer', transition: 'all 0.15s',
        color: '#475569', fontSize: '13px', fontWeight: 500,
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function ProfileIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>;
}
function SettingsIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>;
}
function SecurityIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}
function SignOutIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(setUser);
    return unsub;
  }, []);

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      background: '#F8FAFB',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '40px 24px 80px',
      position: 'relative',
    }}>
      {/* ─── Top header bar ───────────────────────────────────────── */}
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        height: '56px',
        background: '#FFFFFF',
        borderBottom: '1px solid #E2E8F0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 28px',
        zIndex: 50,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '28px', objectFit: 'contain' }} />
          <div style={{ width: '1px', height: '20px', background: '#E2E8F0' }} />
          <span style={{ color: '#64748B', fontSize: '13px', fontWeight: 500 }}>
            EHR Integration Demo
          </span>
        </div>

        {user && <ProfileDropdown user={user} />}
      </div>

      {/* Brand */}
      <div style={{ textAlign: 'center', marginBottom: '48px', marginTop: '56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '4px', height: '36px', borderRadius: '2px', background: 'linear-gradient(180deg, #059669, #10B981)' }} />
          <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0F172A', letterSpacing: '0.08em', margin: 0 }}>
            RAAPID
          </h1>
        </div>
        <p style={{ color: '#475569', fontSize: '15px', fontWeight: 500, margin: '0 0 6px 0' }}>
          Medicare Advantage HCC Coding
        </p>
        <p style={{ color: '#94A3B8', fontSize: '13px', margin: 0 }}>
          Select an EHR integration to launch the interactive demo
        </p>
      </div>

      {/* Generic EHR — top row, centered, wider as primary */}
      <div style={{ display: 'flex', justifyContent: 'center', maxWidth: '860px', width: '100%' }}>
        <EHRCard
          name="Generic EHR"
          subtitle="Point-of-Care Integration"
          description="Vendor-neutral demo showing how RAAPID embeds into any EHR system for point-of-care risk adjustment."
          accentColor="#3B82F6"
          lightColor="#EFF6FF"
          badgeColor="#1D4ED8"
          logo={<GenericLogo />}
          features={['Universal iframe embed', 'Works with any EHR vendor', 'CRN-PAT-00482 · Margaret Thompson']}
          onClick={() => navigate('/generic')}
          wide
        />
      </div>

      {/* Cerner + eCW + OpenEMR — bottom row */}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '1200px', width: '100%', marginTop: '24px' }}>
        <EHRCard
          name="OpenEMR"
          subtitle="SMART on FHIR · Open Source"
          description="Full SMART on FHIR integration with the open-source EHR. Native module with clinical sidebar and FHIR write-back."
          accentColor="#28A745"
          lightColor="#D4EDDA"
          badgeColor="#1E7E34"
          logo={<OpenEmrLogo />}
          features={['SMART on FHIR v2.2.0', 'FHIR R4 write-back', 'CRN-PAT-00482 · Margaret Thompson']}
          onClick={() => navigate('/openemr')}
        />

        <EHRCard
          name="Cerner PowerChart"
          subtitle="mPages Embedded Panel"
          description="RAAPID appears as a native mPages tab inside PowerChart. The clinician never leaves their EHR workflow."
          accentColor="#0079C1"
          lightColor="#E8F4FC"
          badgeColor="#0057A8"
          logo={<CernerLogo />}
          features={['SMART on FHIR launch', 'mPages iframe embed', 'CRN-PAT-00482 · Margaret Thompson']}
          onClick={() => navigate('/cerner')}
        />

        <EHRCard
          name="eCW Healow"
          subtitle="Encounter Tab Integration"
          description="RAAPID appears as an encounter tab in the Healow chart. Multi-tenant per-practice deployment."
          accentColor="#00897B"
          lightColor="#E8F5F3"
          badgeColor="#00695C"
          logo={<EcwLogo />}
          features={['SMART on FHIR + client_secret', 'Encounter tab embed', 'ECW-PAT-01293 · Robert Garcia']}
          onClick={() => navigate('/ecw')}
        />
      </div>

      {/* Direct panel link */}
      <div style={{ marginTop: '48px', textAlign: 'center' }}>
        <button
          onClick={() => navigate('/app')}
          style={{
            background: 'transparent',
            border: '1px solid #CBD5E1',
            color: '#64748B',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { (e.target as HTMLButtonElement).style.borderColor = '#059669'; (e.target as HTMLButtonElement).style.color = '#059669'; }}
          onMouseLeave={e => { (e.target as HTMLButtonElement).style.borderColor = '#CBD5E1'; (e.target as HTMLButtonElement).style.color = '#64748B'; }}
        >
          Open standalone panel →
        </button>
        <p style={{ color: '#94A3B8', fontSize: '11px', marginTop: '8px' }}>
          Direct RAAPID panel · for SMART launch & Chrome extension
        </p>
      </div>

      {/* Footer */}
      <p style={{ marginTop: '48px', color: '#94A3B8', fontSize: '11px', textAlign: 'center' }}>
        RAAPID v1.0 · Demo Environment · Synthetic patient data only
      </p>
    </div>
  );
}

// ─── EHR Card ─────────────────────────────────────────────────────────────────

function EHRCard({
  name, subtitle, description, accentColor, lightColor, badgeColor,
  logo, features, onClick, wide,
}: {
  name: string; subtitle: string; description: string;
  accentColor: string; lightColor: string; badgeColor: string;
  logo: React.ReactNode; features: string[];
  onClick: () => void;
  wide?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={`Launch ${name} demo`}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        padding: '24px 24px 20px',
        width: wide ? '580px' : '380px',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={e => {
        const el = e.currentTarget;
        el.style.borderColor = accentColor;
        el.style.transform = 'translateY(-2px)';
        el.style.boxShadow = `0 4px 16px ${accentColor}18`;
      }}
      onMouseLeave={e => {
        const el = e.currentTarget;
        el.style.borderColor = '#E2E8F0';
        el.style.transform = 'translateY(0)';
        el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
      }}
    >
      {/* Accent bar top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: accentColor, borderRadius: '12px 12px 0 0' }} />

      {/* Logo + badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          background: lightColor,
          borderRadius: '10px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {logo}
        </div>
        <span style={{
          background: badgeColor,
          color: 'white',
          fontSize: '10px',
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: '12px',
          letterSpacing: '0.04em',
        }}>
          {subtitle.toUpperCase()}
        </span>
      </div>

      {/* Title + description */}
      <h2 style={{ color: '#0F172A', fontSize: '18px', fontWeight: 700, margin: '0 0 6px 0' }}>{name}</h2>
      <p style={{ color: '#64748B', fontSize: '13px', lineHeight: 1.5, margin: '0 0 16px 0' }}>{description}</p>

      {/* Features */}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
        {features.map((f) => (
          <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748B', fontSize: '12px', marginBottom: '5px' }}>
            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: accentColor, flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: '14px',
        borderTop: '1px solid #F1F5F9',
      }}>
        <span style={{ color: accentColor, fontSize: '13px', fontWeight: 700 }}>Launch Demo</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

// ─── Logos ────────────────────────────────────────────────────────────────────

function CernerLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ display: 'flex', gap: '3px' }}>
        <div style={{ width: '6px', height: '18px', borderRadius: '3px', background: '#0079C1' }} />
        <div style={{ width: '6px', height: '14px', borderRadius: '3px', background: '#0057A8', marginTop: '4px' }} />
        <div style={{ width: '6px', height: '18px', borderRadius: '3px', background: '#0079C1' }} />
      </div>
      <span style={{ fontSize: '14px', fontWeight: 800, color: '#0079C1', letterSpacing: '0.02em' }}>Cerner</span>
    </div>
  );
}

function EcwLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '6px',
        background: '#00897B',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: 'white', fontSize: '11px', fontWeight: 900 }}>eCW</span>
      </div>
      <span style={{ fontSize: '14px', fontWeight: 800, color: '#00897B' }}>Healow</span>
    </div>
  );
}

function OpenEmrLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%',
        background: '#28A745',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: 'white', fontSize: '14px', fontWeight: 900 }}>O</span>
      </div>
      <span style={{ fontSize: '14px', fontWeight: 800, color: '#28A745' }}>OpenEMR</span>
    </div>
  );
}

function GenericLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '6px',
        background: '#3B82F6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: 'white', fontSize: '14px', fontWeight: 900 }}>+</span>
      </div>
      <span style={{ fontSize: '14px', fontWeight: 800, color: '#3B82F6' }}>EHR</span>
    </div>
  );
}
